import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  isLineBrowser,
  isFacebookBrowser,
  isInAppBrowser,
  getInAppBrowserName,
  openInExternalBrowser, 
  copyToClipboard, 
  shouldShowBrowserPrompt, 
  shouldAutoRedirect,
  dismissBrowserPrompt, 
  disableAutoRedirect,
  isMobileDevice 
} from '../../utils/browserDetector';
import { useTranslation } from 'react-i18next';

const LineBrowserPrompt = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasAttemptedRedirect = useRef(false);
  const hasInitialized = useRef({});
  const browserName = getInAppBrowserName();

  useEffect(() => {
    const currentPath = location.pathname;
    
    // Reset state เมื่อ route เปลี่ยน
    setShowPrompt(false);
    hasAttemptedRedirect.current = false;
    
    // Logic: ถ้าไม่ใช่ In-App Browser (LINE/Facebook Messenger) → ไม่ต้องแสดง prompt เลย
    // เพราะเป็นเว็บให้ข้อมูล ใช้งานได้ดีใน web browser ปกติ (Chrome, Safari, Firefox, etc.)
    if (!isInAppBrowser()) {
      return;
    }
    
    // สำหรับหน้า public pages (/nurse/home, /) ไม่ต้องแสดง prompt
    // เพราะเป็นเว็บให้ข้อมูล ใช้งานได้ดีใน In-App Browser
    const isPublicPage = currentPath === '/' || currentPath === '/nurse/home' || currentPath === '/nurse/landing';
    if (isPublicPage) {
      return;
    }
    
    // ป้องกันการ initialize ซ้ำๆ สำหรับ path เดียวกัน
    if (hasInitialized.current[currentPath]) {
      return;
    }
    
    // Mark as initialized สำหรับ path นี้
    hasInitialized.current[currentPath] = true;
    
    // ถ้าเป็น In-App Browser (LINE/Facebook Messenger) → แสดง prompt เหมือนเดิม
    if (shouldShowBrowserPrompt()) {
      // ถ้า user ไม่ได้ปิด auto-redirect ให้ลองเปิดใน browser ภายนอกทันที
      if (shouldAutoRedirect() && !hasAttemptedRedirect.current) {
        hasAttemptedRedirect.current = true;
        
        // Delay เล็กน้อยเพื่อให้หน้าเว็บโหลดเสร็จก่อน
        // สำหรับหน้า "/" ให้ delay นานขึ้นเพื่อให้ user เห็นหน้า welcome ก่อน
        // สำหรับหน้า "/nurse" ให้ delay เพื่อให้ user เห็นหน้าแอพก่อน
        const delay = currentPath === '/' ? 1500 : currentPath.startsWith('/nurse') ? 1000 : 500;
        
        const timer = setTimeout(() => {
          const success = openInExternalBrowser();
          
          // ถ้าเปิดไม่ได้ (user ปฏิเสธ หรือ error) ให้แสดง modal
          if (!success) {
            setShowPrompt(true);
          } else {
            // ถ้าเปิดได้ ให้รอสักครู่แล้วตรวจสอบว่ายังอยู่ใน In-App Browser หรือไม่
            // ถ้ายังอยู่แสดงว่าเปิดไม่ได้จริงๆ
            setTimeout(() => {
              if (isInAppBrowser()) {
                setShowPrompt(true);
              }
            }, 2000);
          }
        }, delay);
        
        return () => clearTimeout(timer);
      } else {
        // ถ้า user ปิด auto-redirect ไว้แล้ว ให้แสดง modal เลย
        // สำหรับหน้า "/" ให้ delay นานขึ้น
        // สำหรับหน้า "/nurse" ให้ delay เพื่อให้ user เห็นหน้าแอพก่อน
        const delay = currentPath === '/' ? 2000 : currentPath.startsWith('/nurse') ? 1500 : 1000;
        
        const timer = setTimeout(() => {
          setShowPrompt(true);
        }, delay);
        
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname]);

  const handleOpenInBrowser = () => {
    const success = openInExternalBrowser();
    if (success) {
      // ปิด modal หลังจากเปิด browser แล้ว
      setTimeout(() => {
        setShowPrompt(false);
      }, 500);
    }
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard();
    if (success) {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };

  const handleDismiss = () => {
    dismissBrowserPrompt();
    setShowPrompt(false);
  };

  const handleUseInApp = () => {
    // User ต้องการใช้ใน In-App Browser ต่อไป
    disableAutoRedirect();
    setShowPrompt(false);
  };

  // Get browser name for display
  const getBrowserDisplayName = () => {
    if (browserName === 'line') return 'LINE';
    if (browserName === 'facebook') return 'Facebook Messenger';
    return 'แอพ';
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fade-in">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-bold text-gray-900 text-center mb-2">
          {t('lineBrowserPrompt.title', 'เปิดใน Browser เพื่อประสบการณ์ที่ดีกว่า')}
        </h2>

        {/* Description */}
        <p className="text-gray-600 text-center mb-6 text-sm">
          {t('lineBrowserPrompt.description', 'เพื่อความสะดวกและประสิทธิภาพในการใช้งาน แนะนำให้เปิด NurseApp ใน Browser ภายนอก (Chrome, Safari)')}
        </p>

        {/* Info message if auto-redirect failed */}
        {hasAttemptedRedirect.current && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-800">
            {t('lineBrowserPrompt.redirectFailed', 'ไม่สามารถเปิดใน Browser อัตโนมัติได้ กรุณากดปุ่มด้านล่าง')}
          </div>
        )}

        {/* Buttons */}
        <div className="space-y-3">
          {/* Primary Button - Open in Browser */}
          <button
            onClick={handleOpenInBrowser}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            {t('lineBrowserPrompt.openInBrowser', 'เปิดใน Browser')}
          </button>

          {/* Secondary Button - Copy Link */}
          <button
            onClick={handleCopyLink}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
          >
            {copied ? (
              <>
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t('lineBrowserPrompt.copied', 'คัดลอกแล้ว!')}
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                {t('lineBrowserPrompt.copyLink', 'คัดลอก Link')}
              </>
            )}
          </button>

          {/* Use in In-App Browser Button */}
          <button
            onClick={handleUseInApp}
            className="w-full text-gray-500 hover:text-gray-700 text-sm py-2 transition-colors duration-200"
          >
            {t('lineBrowserPrompt.useInApp', `ใช้ใน ${getBrowserDisplayName()} ต่อไป`, { browser: getBrowserDisplayName() })}
          </button>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="w-full text-gray-400 hover:text-gray-600 text-xs py-1 transition-colors duration-200"
          >
            {t('lineBrowserPrompt.dontShowAgain', 'ไม่แสดงอีก')}
          </button>
        </div>
      </div>

      {/* Animation CSS */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LineBrowserPrompt;

